/**
 * V4BottomDock — Phase 7c-7.
 *
 * The "B" half of the Calm-Pro-+-Mission-Control hybrid. Slides up
 * under the canvas when a run is in flight or recently opened, hosting
 * the run timeline / output / logs surface. Right rail stays focused
 * on the Inspector so the operator can keep editing while watching.
 *
 *   ┌─ canvas region ─────────────────────────────────┐
 *   │                                                  │
 *   │  Start → HTTP → End                              │
 *   │                                                  │
 *   ├─────────────────────────────────────────────────┤  ← dock divider
 *   │ ◐ Run e841156a · Running 2s              ▲│✕    │  ← header bar
 *   │ ─────────────────────────────────────────────── │
 *   │ Timeline │ Output │ Logs                         │  ← tabs
 *   │ ✓ Start              77ms                       │
 *   │ ◐ HTTP Request       0.6s                       │
 *   │ ◌ End                ─                          │
 *   └─────────────────────────────────────────────────┘
 *
 * Design notes:
 *   • Collapsible — operator can shrink to a header-only strip while
 *     the run is in progress if they want canvas focus back.
 *   • Auto-shows on a fresh run start; auto-stays-open on terminal
 *     status so the operator can review.
 *   • Re-uses V4RunDetailPanel internals via the embedded mode rather
 *     than re-implementing — the timeline/output/logs tabs are already
 *     tested + polished there.
 *
 * Spread-preservation contract: this component is presentational only.
 * Run data comes from the existing V4RunDetailPanel polling. No new
 * API calls; no new state to lose.
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import V4RunDetailPanel from './V4RunDetailPanel';
import { S, SPACING, RADIUS, FONT, WEIGHT } from './v4Tokens';

const DOCK_HEIGHT_EXPANDED = 260;
const DOCK_HEIGHT_COLLAPSED = 36;

export default function V4BottomDock({
  runId,
  onClose,
  onJumpToNode,
  // Phase 7q-4/5 — current canvas selection, threaded into the run
  // detail panel so the Input tab (and per-node debug surfaces) can
  // show the selected node's per-task execution state.
  selectedCanvasNodeId = null,
}) {
  const [collapsed, setCollapsed] = useState(false);

  if (!runId) return null;

  return (
    <div
      role="region"
      aria-label="Run execution dock"
      style={{
        flexShrink: 0,
        height: collapsed ? DOCK_HEIGHT_COLLAPSED : DOCK_HEIGHT_EXPANDED,
        borderTop: `1px solid ${S[200]}`,
        background: '#ffffff',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        transition: 'height 200ms ease',
      }}
    >
      {/* Header bar — always visible. Collapse toggle + close X. */}
      <div style={{
        display: 'flex', alignItems: 'center',
        gap: SPACING.s,
        padding: `${SPACING.xs + 2}px ${SPACING.m}px`,
        background: S[50],
        borderBottom: collapsed ? 'none' : `1px solid ${S[200]}`,
        flexShrink: 0,
      }}>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          title={collapsed ? 'Expand run detail' : 'Collapse run detail'}
          aria-label={collapsed ? 'Expand run detail' : 'Collapse run detail'}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 20, height: 20, padding: 0,
            color: S[600], background: 'transparent',
            border: 'none', borderRadius: RADIUS.sm,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          {collapsed
            ? <ChevronUp size={13} aria-hidden="true" />
            : <ChevronDown size={13} aria-hidden="true" />
          }
        </button>
        <span style={{
          fontSize: FONT.sm, fontWeight: WEIGHT.semibold, color: S[800],
        }}>
          Run detail
        </span>
        <code style={{
          fontSize: FONT.xs, color: S[500],
          fontFamily: 'SFMono-Regular, Menlo, monospace',
        }}>
          {String(runId).slice(0, 8)}
        </code>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => { setCollapsed(false); onClose && onClose(); }}
          title="Close run detail"
          aria-label="Close run detail"
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 22, height: 22, padding: 0,
            color: S[600], background: '#ffffff',
            border: `1px solid ${S[200]}`, borderRadius: RADIUS.sm,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <X size={11} aria-hidden="true" />
        </button>
      </div>

      {/* Expanded body — embeds V4RunDetailPanel without its own header
          (we render our own above for the collapse/close affordances).
          The panel's internal header is hidden by passing onClose=null
          and relying on the embedded prop to strip floating chrome. */}
      {!collapsed && (
        <div style={{
          flex: 1, minHeight: 0,
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}>
          <V4RunDetailPanel
            runId={runId}
            onClose={null /* dock owns close; panel just renders body */}
            onJumpToNode={onJumpToNode}
            selectedCanvasNodeId={selectedCanvasNodeId}
            embedded
          />
        </div>
      )}
    </div>
  );
}
